import { useState } from "react";
import { Globe, Image as ImageIcon, DollarSign, Search } from "lucide-react";
import PortfolioModule from "./PortfolioModule";
import PricingModule from "./PricingModule";
import SeoModule from "./SeoModule";
import useIsMobile from "../utils/useIsMobile";

const TABS = [
  { id: "portfolio", label: "Portfolio", icon: ImageIcon, perm: "portfolio" },
  { id: "pricing", label: "Pricing & Services", icon: DollarSign, perm: "pricing" },
  { id: "seo", label: "SEO & Meta", icon: Search, perm: "portfolio" },
];

/**
 * Unified "Website" admin module — Portfolio + Pricing + SEO under a single tabbed page.
 * Matches the Workforce module's tab pattern for visual consistency.
 *
 * Props:
 *   canEdit: (perm: string) => boolean — used to pass readOnly to child modules
 *   initialTab: "portfolio" | "pricing" | "seo" (default "portfolio")
 */
export default function WebsiteModule({ canEdit = () => true, initialTab = "portfolio" }) {
  const [tab, setTab] = useState(TABS.some(t => t.id === initialTab) ? initialTab : "portfolio");
  const isMobile = useIsMobile();

  return (
    <div className="space-y-4" data-testid="website-module">
      <div>
        <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
          <Globe className="w-6 h-6 text-forest-700" /> Website
        </h2>
        <p className="text-sm text-stone-500 mt-1">Manage the public site — portfolio, pricing, and SEO metadata.</p>
      </div>

      <div className={`flex gap-2 border-b border-stone-200 ${isMobile ? "overflow-x-auto -mx-4 px-4 pb-1" : ""}`}>
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`website-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors min-h-[44px] ${
                tab === t.id
                  ? "border-forest-600 text-forest-800"
                  : "border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div data-testid={`website-panel-${tab}`}>
        {tab === "portfolio" && <PortfolioModule readOnly={!canEdit("portfolio")} />}
        {tab === "pricing" && <PricingModule readOnly={!canEdit("pricing")} />}
        {tab === "seo" && <SeoModule readOnly={!canEdit("portfolio")} />}
      </div>
    </div>
  );
}
